import type { LocationObjectCoords } from 'expo-location';

import { logger } from '@garnish/logger';
import getBounds from 'geolib/es/getBounds';
import getCenterOfBounds from 'geolib/es/getCenterOfBounds';

import type { Coordinate, PinData, Region, ViewportBounds } from './types';

export const DEFAULT_ZOOM_LEVEL = 13;
export const EMPTY_REGION = { lat: 0, lng: 0, zoom: DEFAULT_ZOOM_LEVEL };

// Given a {Region} array, return its center/middle {Region}
export function findCenterRegion(
  locations: readonly Region[],
  mapDimensions?: Readonly<{ width: number; height: number }>,
): Region {
  if (locations.length === 0) return EMPTY_REGION;
  const coordinates = getCoordinates(locations);

  return {
    ...findCenterPoint(coordinates),
    zoom: findZoomLevel(coordinates, mapDimensions),
  };
}

// Given a {Region} array, return a {Coordinate} array.
export function getCoordinates(locations: readonly Region[]): readonly Coordinate[] {
  return locations
    .filter((location) => location.lat && location.lng)
    .map((location) => ({
      latitude: location.lat,
      longitude: location.lng,
    }));
}

// Given a {Coordinate} array, return its center/middle {Region}
export function findCenterPoint(coordinates: readonly Coordinate[]): Region {
  const center = coordinates.length > 0 ? getCenterOfBounds([...coordinates]) : null;

  if (!center) return { lat: 0, lng: 0 };

  return {
    lat: center.latitude,
    lng: center.longitude,
  };
}

// Given a {Coordinate} array, return its zoom level {number}
// Reference: https://stackoverflow.com/a/13274361
export function findZoomLevel(
  coordinates: readonly Coordinate[],
  mapDimensions?: Readonly<{ width: number; height: number }>,
): number {
  if (coordinates.length === 0) return 16;

  const { width: mapWidth = 0, height: mapHeight = 0 } = mapDimensions ?? {};

  const WORLD_DIM = { width: 256, height: 256 };
  const SCREEN_DIM = {
    width: mapWidth > 0 ? mapWidth : 800,
    height: mapHeight > 0 ? mapHeight : 400,
  };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;

    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log2(mapPx / worldPx / fraction));
  }

  const { minLat, maxLat, minLng, maxLng } = getBounds([...coordinates]);

  const latFraction = (latRad(maxLat) - latRad(minLat)) / Math.PI;

  const lngDiff = maxLng - minLng;
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(SCREEN_DIM.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(SCREEN_DIM.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

export function findRegionBounds(region: Partial<NativeRegion>): ViewportBounds {
  const { latitude = 0, longitude = 0, latitudeDelta = 0.5, longitudeDelta = 0.5 } = region;

  return {
    topLeft: {
      latitude: latitude + latitudeDelta / 2,
      longitude: longitude - longitudeDelta / 2,
    },
    bottomRight: {
      latitude: latitude - latitudeDelta / 2,
      longitude: longitude + longitudeDelta / 2,
    },
  };
}

// ─── Pin Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns an object with pin data the current customer geolocation
 *
 * NOTE: If the provided geolocation is invalid, `undefined` will be returned.
 */
export const getCustomerGeolocationPin = (
  props: GetCustomerGeolocationPinProps,
): PinData | undefined => {
  const { geolocation, customerName = '', selectedPin } = props;
  const { latitude, longitude } = geolocation ?? {};

  const isActivePin = selectedPin === 'customer';

  if (longitude === undefined || latitude === undefined) return;

  // `0, 0` is the fallback callers use for "unknown", not a place a customer is.
  if (latitude === 0 && longitude === 0) return;

  return {
    id: 'customer',
    name: customerName,
    active: isActivePin,
    lat: latitude,
    lng: longitude,
    isCustomer: true,
  };
};

// ────────────────────────────────────────────────────────────────────────────────

export const log = logger.extend('MAP');

// ─── Types ──────────────────────────────────────────────────────────────────────

type GetCustomerGeolocationPinProps = Readonly<{
  geolocation?: Partial<LocationObjectCoords>;
  customerName?: string;
  selectedPin?: string;
}>;

type NativeRegion = Readonly<{
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
}>;
